<template>
  <v-container>
    <div class="mx-4 mx-md-0">
      <!-- to align with the margin of Searchbar on mobile -->
      <v-data-iterator
        :items="routes"
        :items-per-page="100"
        hide-default-footer
        :sort-by="sortByValueGetter"
        :custom-sort="sortTransports"
      >
        <template v-slot:header>
          <v-row no-gutters align="end" class="my-4 d-print-none">
            <v-col
              :cols="printView ? undefined : usePoll ? 3 : 4"
              class="d-flex align-center my-0 my-md-3"
            >
              <div>
                <i
                  >Alle Werte gelten für die einfache Strecke.<br />Genug
                  Stauzeit berücksichtigt? Manuell anpassen?</i
                >
              </div>
            </v-col>
            <v-col :cols="usePoll ? 7 : 8">
              <div class="mr-2 text-body-1">
                <v-icon class="mr-1">format_line_spacing</v-icon>
                Sortieren nach:
              </div>
              <v-btn-toggle
                :value="sortByValueGetter"
                color="primary"
                @change="sortByValueChanged($event)"
                background-color="secondary"
                mandatory
                :dense="$vuetify.breakpoint.xs"
                class="rounded-tr-xl sort-btns full-width"
              >
                <v-btn
                  v-for="x in sortByOptions"
                  :key="x.id"
                  :value="x.id"
                  :color="x.id === 'sortByValueGetter' ? 'primary' : ''"
                  :aria-label="'Sortieren nach ' + x.name"
                  text
                  class="col-auto col-lg-3"
                  :x-large="$vuetify.breakpoint.lgAndUp"
                >
                  <v-icon
                    :color="x.id === 'sortByValueGetter' ? 'primary' : ''"
                    class="mr-sm-1"
                    aria-hidden="true"
                    >{{ x.icon }}</v-icon
                  >
                  <span class="d-none d-sm-inline">{{ x.name }}</span>
                </v-btn>
              </v-btn-toggle>
            </v-col>
          </v-row>
        </template>

        <template v-slot:default="props">
          <div role="list">
            <v-row
              v-for="(routing, i) in props.items"
              :key="routingKey(routing)"
              role="listitem"
            >
            <!-- on small screens the poll inputs move below the transport card instead of a 2/12 column -->
            <v-col :cols="usePoll && !$vuetify.breakpoint.smAndDown ? 10 : 12">
              <Transport
                :t="routing"
                :searchParams="searchParams"
                :rideshareHost="rideshareHost"
                :reservationToolHost="reservationToolHost"
                :printView="printView"
                :onlyJourneyThere="onlyJourneyThere"
                :loading="loading"
                :percentageTransportsComputed="percentageTransportsComputed"
              />
            </v-col>
            <v-col v-if="usePoll && pollType == 'company'" :cols="$vuetify.breakpoint.smAndDown ? 12 : 2">
              <PollCompanyInputForm :transportId="routing.transport.id" :transportMode="routing.transport.routingParams.vehicleRoutingMode" :config="config" :routes="routes" />
            </v-col>
            <v-col v-else-if="usePoll && pollType == 'event'" :cols="$vuetify.breakpoint.smAndDown ? 12 : 2">
              <PollEventInputForm :transportId="routing.transport.id"  :transportMode="routing.transport.routingParams.vehicleRoutingMode" :config="config" :routes="routes" />
            </v-col>
            </v-row>
          </div>
        </template>
      </v-data-iterator>
      <v-row v-if="usePoll" justify="center" class="my-8">
        <v-col cols="12" style="align-items: center">
          <PollFooter :config="config" :destination="searchParams.destination" :routes="routes" />
        </v-col>
      </v-row>
      <v-row v-if="showMoreRoutesRow" justify="start" class="my-8">
        <v-col cols="12">
          <div
            class="flex-box-centered-horizontal border-radius-10px padding-10px background-white"
          >
            <div v-if="showMoreRoutesHint">
              <a
                @click="showMoreRoutes()"
                class="cursor-pointer font-size-22px margin-left-15px link-contrast"
                >Kombinationen anzeigen</a
              >
              <v-tooltip top>
                <template v-slot:activator="{on, attrs}"
                  ><v-icon
                    color="primary"
                    size="dense"
                    dark
                    v-bind="attrs"
                    v-on="on"
                    >mdi-help-circle</v-icon
                  ></template
                >
                <span>z.B. ÖPV + Taxi, ÖPV + Car-Sharing</span>
              </v-tooltip>
              <v-icon aria-hidden="true"> arrow_downward </v-icon>
            </div>
          </div>
        </v-col>
      </v-row>
      <v-row justify="center" class="my-8">
        <div class="text-h6 font-weight-light d-flex flex-column align-center">
          <a @click="print" class="link-contrast">
            <v-icon color="primary">print</v-icon>
            Druckansicht
          </a>
        </div>
      </v-row>
      <!-- F-033: Direktlink zurueck zur Suche fuer Tastatur- und SR-Nutzer
           am Ende der (potentiell langen) Ergebnisliste. -->
      <v-row justify="center" class="my-4 d-print-none">
        <a href="#main-content" class="back-to-search-link"
          >Zurück zur Suche</a
        >
      </v-row>
    </div>
  </v-container>
</template>

<script>
import PollFooter from "./poll/PollFooter.vue";

import { wait } from "@norwin/javascript-is-bad";
import { storeToRefs } from "pinia";
import Vue from "vue";
import { useConfigStore } from "../store/config-store";
import { usePollStore } from "../store/poll-store";
import { metricsDisplayParams } from "../ui-mappings";
import PollCompanyInputForm from "./poll/PollCompanyInputForm.vue";
import PollEventInputForm from "./poll/PollEventInputForm.vue";
import Transport from "./Transport.vue";

export default Vue.extend({
  name: "TransportListing",

  components: {
    PollCompanyInputForm,
    PollEventInputForm,
    Transport,
    PollFooter,
  },

  props: {
    config: {type: Object, default: undefined},
    searchParams: {
      type: Object,
      default: () => ({
        origin: "",
        departure: "", // contains Date instances when filled out
        arrival: "",
        destination: "",
        includeIntermodal: false,
        minutesCongestion: "0",
        minutesParking: "0",
        originLonLat: "",
        destinationLonLat: "",
      }),
    },
    routes: {type: Array, default: () => []},
    rideshareHost: String,
    reservationToolHost: String,
    onlyJourneyThere: Boolean,
    showMoreRoutesHint: Boolean,
    showMoreRoutesClicked: {type: Function},
    loading: {type: Boolean, default: false},
    percentageTransportsComputed: {type: Number, default: 0},
  },

  data: () => ({
    metricToSortRoutesBy: {value: "time"},
    showProgressCircluar: false,
    sortByOptions: metricsDisplayParams,
    lastUpdated: null,
    printView: false,
    submitText: "",
  }),

  mounted() {
    this.metricToSortRoutesBy = storeToRefs(useConfigStore()).metricToSortRoutesBy;
  },

  methods: {
    showMoreRoutes() {
      this.showProgressCircluar = true;
      this.showMoreRoutesClicked();
    },
    async print() {
      this.printView = !this.printView;
      if (this.printView) {
        await wait(2000); // give time for map tiles to load
        window.print();
        this.printView = false;
      }
    },
    sortByValueChanged(sortByValue) {
      useConfigStore().setMetricToSortRoutesBy(sortByValue);
    },
    routingKey(r) {
      // used to force rerender of routes
      return `${r.transport.id}`;
    },

    sortTransports(transports) {
      const key = this.sortByValueGetter; //this.sortBy;
      const descending = ["sport"].includes(key);
      return transports.sort((a, b) => {
        let valA = a.metrics[key];
        let valB = b.metrics[key];
        if (descending) {
          valA *= -1;
          valB *= -1;
        }

        return valA - valB;
      });
    },
  },

  computed: {
    /**
     * whether poll feature is used or not
     */
    usePoll() {
      return storeToRefs(usePollStore()).usePoll.value || false;
    },
    /**
     * which poll type is used
     */
    pollType() {
      return storeToRefs(usePollStore()).pollType.value || "company";
    },
    /**
     * if the "show more routes" hint should be displayed
     */
    showMoreRoutesRow: {
      get: function () {
        return this.showMoreRoutesHint;
      },
    },
    /**
     * metric to sort by like co2, time etc.
     */
    sortByValueGetter: {
      get: function () {
        return this.metricToSortRoutesBy.value;
      },
    }
  },

  watch: {
  },
});
</script>

<style scoped>
.full-width {
  width: 100%;
}
.sort-btns >>> button:not(.v-btn--active),
.sort-btns >>> i,
button {
  /* Kontrast: #000 statt #333 – der Text sitzt auf dem Teal-Hintergrund
     (#55a699); #333 erreicht dort nur ~3,2:1 (WCAG 1.4.3 verlangt 4,5:1). */
  color: #000 !important;
}
/* Kontrast: dunkleres Marken-Rot für Fließtext-Links auf weißem Grund –
   das Primaer-Rot #f54a3d erreicht nur 3,54:1. */
.link-contrast {
  color: #c62828 !important;
}
.background-white {
  background-color: white;
}
.padding-10px {
  padding: 10px;
}
.border-radius-10px {
  border-radius: 10px;
}
.flex-box-centered-horizontal {
  display: flex;
  flex-direction: row;
  align-items: center;
}
.cursor-pointer {
  cursor: pointer;
}
.font-size-22px {
  font-size: 22px;
}
.margin-left-15px {
  margin-left: 15px;
}
</style>
